import { useCallback, useState } from "react";

import { Button, InfoBar, Modal } from "@shapr3d/frontend-lib";
import toast from "react-hot-toast";

import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { ModalClosedAction, ModalShownAction } from "../../lib/TrackingAdapter";

import style from "./RecoveryCodeModal.module.css";

export type RecoveryCodeModalProps = {
  open: boolean;
  recoveryCode: string;
  onClose: () => void;
};

export default function RecoveryCodeModal({ open, recoveryCode, onClose }: RecoveryCodeModalProps) {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      setCopied(true);
      toast.success(t(m.TwoFactorAuth.Recovery.CodeCopied));
    } catch (error) {
      toast.error(t(m.TwoFactorAuth.Recovery.CodeCopyFailed));
      setCopied(false);
    }
  }, [recoveryCode, t, m]);

  const handleDownload = useCallback(() => {
    const blob = new Blob([recoveryCode], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "shapr3d-recovery-code.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setDownloaded(true);

    toast.success(t(m.TwoFactorAuth.Recovery.CodeDownloaded));
  }, [recoveryCode, t, m]);

  const canProceed = copied || downloaded;

  return (
    <Modal
      open={open}
      onOpen={() => tracker.trackModalShown(ModalShownAction.TWO_FACTOR_RECOVERY_CODE)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.TWO_FACTOR_RECOVERY_CODE);
        onClose();
      }}
      size="medium"
      title={t(m.TwoFactorAuth.Recovery.Title)}
      persistent
      footer={
        <div className={style.footer}>
          <Button variant="primary" onClick={onClose} disabled={!canProceed}>
            {t(m.TwoFactorAuth.Recovery.SavedCode)}
          </Button>
        </div>
      }
    >
      <div className={style.recoveryCodeContainer}>
        <div className={style.recoveryCode}>{recoveryCode}</div>
        <div className={style.buttonGroup}>
          <Button variant="grayscale" onClick={handleCopy}>
            {t(m.TwoFactorAuth.Recovery.CopyToClipboard)}
          </Button>
          <Button variant="grayscale" onClick={handleDownload}>
            {t(m.TwoFactorAuth.Recovery.DownloadAsTxt)}
          </Button>
        </div>
      </div>
      <InfoBar
        variant="critical"
        title={t(m.TwoFactorAuth.Recovery.Important)}
        text={
          <>
            <p>{t(m.TwoFactorAuth.Recovery.SaveDescription)}</p>
            <p>
              <b>{t(m.TwoFactorAuth.Recovery.SecurityWarning)}</b>
            </p>
          </>
        }
      />
    </Modal>
  );
}
